/**
 * @file feed-days.ts
 * @description Раскладка ленты по дням и подписи времени по поясу магазина
 * (phase19.31, uiux-design.md §4.1; SC31–SC32).
 *
 * Чистые функции: пояс и «сейчас» приходят параметрами, поэтому заголовки
 * «Сегодня / Вчера» проверяются тестами без подмены часов. День считается по
 * поясу МАГАЗИНА, а не браузера: событие в 23:30 по Екатеринбургу у
 * сотрудника из Москвы не должно уехать в «завтра».
 */

import type { FeedDayGroup, FeedEntry } from "./feed-types";

/**
 * Ключ запасной группы для строк с непонятной датой. `Intl.format` на
 * Invalid Date бросает `RangeError`, и одна битая строка роняла всю ленту
 * (ревью phase19.31) — такие строки собираем в «Дата не указана».
 */
export const FEED_NO_DATE_KEY = "no-date";

/** `YYYY-MM-DD` дня момента по поясу (формат `en-CA` даёт ровно его). */
export function feedDayKey(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return FEED_NO_DATE_KEY;
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
}

/** Ключ дня, предшествующего `key` (календарно, без часовых поясов). */
function previousDayKey(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  const prev = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) - 1));
  return prev.toISOString().slice(0, 10);
}

/** «2 октября» (или «2 октября 2025», если год не текущий) по ключу дня. */
function dayText(key: string, withYear: boolean): string {
  const [y, m, d] = key.split("-").map(Number);
  // Полдень UTC: дата по ключу не уедет ни в каком поясе форматирования.
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12));
  return date.toLocaleDateString("ru-RU", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    ...(withYear ? { year: "numeric" } : {}),
  }).replace(/\s?г\.$/, "");
}

/** Заголовок дня: «Сегодня, 2 октября», «Вчера, 1 октября», «28 сентября». */
export function feedDayLabel(key: string, now: Date, timeZone: string): string {
  if (key === FEED_NO_DATE_KEY) return "Дата не указана";
  const today = feedDayKey(now.toISOString(), timeZone);
  if (key === today) return `Сегодня, ${dayText(key, false)}`;
  if (key === previousDayKey(today)) return `Вчера, ${dayText(key, false)}`;
  return dayText(key, key.slice(0, 4) !== today.slice(0, 4));
}

/** Время события «22:00» по поясу магазина (дата — в заголовке дня). */
export function feedTimeLabel(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("ru-RU", { timeZone, hour: "2-digit", minute: "2-digit" });
}

/**
 * Разложить строки по дням, сохраняя порядок сервера (новые сверху).
 * Соседние строки одного дня — в одну группу.
 */
export function groupFeedByDay(entries: readonly FeedEntry[], now: Date, timeZone: string): FeedDayGroup[] {
  const groups: FeedDayGroup[] = [];
  for (const entry of entries) {
    const key = feedDayKey(entry.at, timeZone);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.entries.push(entry);
    } else {
      groups.push({ key, label: feedDayLabel(key, now, timeZone), entries: [entry] });
    }
  }
  return groups;
}
